import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { PantheraImport } from '../../types';
import { apiClient } from '../../services/apiClient';
import './SelectionModal.css';

interface PantheraImportModalProps {
  reportId: string;
  onClose: () => void;
  /** Via d'uscita quando Panthera non risponde: apre l'import da file CSV */
  onSwitchToCsv: () => void;
}

/**
 * PROD-499: import ricambi da documento Panthera. La richiesta e' asincrona: il backend la
 * inoltra al servizio interno Proform (PPL) e il modale ne interroga lo stato finche' non e'
 * conclusa. Il risultato ha la stessa forma dell'import CSV multi-macchina.
 */
type ModalStatus = 'idle' | 'waiting' | 'done';

/** Esito da mostrare sotto il campo, lasciando il numero documento compilato per correggerlo o riprovare */
type Notice =
  | 'not_found'
  | 'empty'
  | 'unreachable'
  | 'panthera_error'
  | 'interrupted'
  | 'still_processing'
  | 'failed'
  | 'invalid_document_number'
  | 'fatal';

export const POLL_INTERVAL_MS = 2000;
/** Oltre il timeout lato server (90s) piu' un margine: se il backend non risponde si rinuncia qui */
export const MAX_WAIT_MS = 120000;

const IN_PROGRESS = ['PENDING', 'PROCESSING'];
const UNREACHABLE_CODES = ['panthera_publish_failed', 'panthera_timeout'];

const formatDateTime = (iso?: string) => (iso ? format(new Date(iso), 'dd/MM/yyyy HH:mm') : '');
const formatDate = (iso?: string) => (iso ? format(new Date(iso), 'dd/MM/yyyy') : '');

const PantheraImportModal: React.FC<PantheraImportModalProps> = ({ reportId, onClose, onSwitchToCsv }) => {
  const { t } = useTranslation();
  const [documentNumber, setDocumentNumber] = useState('');
  const [status, setStatus] = useState<ModalStatus>('idle');
  const [current, setCurrent] = useState<PantheraImport | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [pollTick, setPollTick] = useState(0);
  const startedAt = useRef<number>(0);

  const conclude = (imp: PantheraImport) => {
    setCurrent(imp);
    if (imp.status === 'COMPLETED') {
      setStatus('done');
      return;
    }
    const outcome: Notice =
      imp.status === 'NOT_FOUND' ? 'not_found'
        : imp.status === 'EMPTY' ? 'empty'
          : imp.status === 'INTERRUPTED' ? 'interrupted'
            : imp.errorCode === 'panthera_error' ? 'panthera_error'
              : imp.status === 'EXPIRED' || UNREACHABLE_CODES.includes(imp.errorCode ?? '') ? 'unreachable'
                : 'failed';
    setNotice(outcome);
    setStatus('idle');
  };

  // Interroga lo stato della richiesta finche' non e' conclusa o scade l'attesa massima.
  // `pollTick` riarma il giro successivo anche quando la risposta non cambia.
  const importId = current?.id;
  useEffect(() => {
    if (status !== 'waiting' || !importId) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (Date.now() - startedAt.current > MAX_WAIT_MS) {
        // Se le righe erano gia' in scrittura, Panthera ha risposto: il problema e' l'esito, non la connessione.
        setNotice(current?.status === 'PROCESSING' ? 'still_processing' : 'unreachable');
        setStatus('idle');
        return;
      }
      try {
        const imp = await apiClient.getPantheraImport(reportId, importId);
        if (cancelled) return;
        if (IN_PROGRESS.includes(imp.status)) {
          setCurrent(imp);
          setPollTick((n) => n + 1);
        } else {
          conclude(imp);
        }
      } catch {
        // Errore transitorio di rete: si riprova al giro successivo, entro l'attesa massima.
        if (!cancelled) setPollTick((n) => n + 1);
      }
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, importId, pollTick, reportId]);

  const handleImport = async () => {
    if (!documentNumber.trim()) return;
    setNotice(null);
    setFatalError(null);
    setStatus('waiting');
    startedAt.current = Date.now();
    try {
      const imp = await apiClient.requestPantheraImport(reportId, documentNumber.trim());
      setCurrent(imp);
      if (imp.duplicate && imp.status === 'COMPLETED') {
        setStatus('done');
      } else if (!IN_PROGRESS.includes(imp.status)) {
        conclude(imp);
      }
    } catch (err: any) {
      const code = err?.message || 'unknown_error';
      setNotice(code === 'invalid_document_number' ? 'invalid_document_number' : 'fatal');
      setFatalError(code);
      setStatus('idle');
    }
  };

  const handleOverlayClick = () => {
    if (status !== 'waiting') onClose();
  };

  const renderNotice = () => {
    if (!notice) return null;
    const doc = current?.documentNumber ?? documentNumber.trim();
    const text =
      notice === 'fatal'
        ? t(`messages.pantheraImportError.${fatalError}`, fatalError ?? '')
        : notice === 'failed'
          ? t('reportForm.pantheraImportModal.notice.failed', {
            reason: t(`messages.pantheraImportError.${current?.errorCode}`, current?.errorMessage ?? current?.errorCode ?? ''),
          })
          : t(`reportForm.pantheraImportModal.notice.${notice}`, { document: doc });
    return (
      <div className="error-message panthera-import-notice" role="alert">
        <strong>{t(`reportForm.pantheraImportModal.noticeTitle.${notice}`)}</strong>
        <p>{text}</p>
      </div>
    );
  };

  const renderResult = (imp: PantheraImport) => {
    if (imp.duplicate) {
      return (
        <div className="error-message panthera-import-notice" role="alert">
          <strong>{t('reportForm.pantheraImportModal.noticeTitle.duplicate')}</strong>
          <p>
            {t('reportForm.pantheraImportModal.notice.duplicate', {
              document: imp.documentNumber,
              date: formatDateTime(imp.completedAt ?? imp.requestedAt),
              user: imp.requestedByName ?? '—',
              count: imp.insertedCount ?? 0,
            })}
          </p>
        </div>
      );
    }
    const result = imp.result;
    return (
      <div className="csv-import-results">
        <p>
          {t('reportForm.pantheraImportModal.documentLine', {
            document: imp.pantheraDocument ?? imp.documentNumber,
            date: formatDate(imp.documentDate),
          })}
        </p>
        <h4>{t('reportForm.multiMachineCsvImportModal.results')}</h4>
        <table className="spareparts-table csv-summary-table">
          <tbody>
            <tr>
              <td>{t('reportForm.multiMachineCsvImportModal.totalRows')}</td>
              <td><strong>{imp.totalRows}</strong></td>
            </tr>
            <tr>
              <td>{t('reportForm.multiMachineCsvImportModal.insertedCount')}</td>
              <td><strong>{imp.insertedCount}</strong></td>
            </tr>
            <tr>
              <td>{t('reportForm.multiMachineCsvImportModal.errorCount')}</td>
              <td><strong>{imp.errorCount}</strong></td>
            </tr>
          </tbody>
        </table>

        {imp.machines && imp.machines.length > 0 && (
          <>
            <h4>{t('reportForm.pantheraImportModal.machinesTable')}</h4>
            <div className="spareparts-table-container">
              <table className="spareparts-table">
                <thead>
                  <tr>
                    <th>{t('reportForm.pantheraImportModal.colCommessa')}</th>
                    <th>{t('reportForm.pantheraImportModal.colRows')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {imp.machines.map((m) => (
                    <tr key={m.reportMachineId}>
                      <td>{m.serialNumber}</td>
                      <td>{m.rowCount}</td>
                      <td>{m.addedToReport ? t('reportForm.pantheraImportModal.addedToReport') : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {result && result.errors.length > 0 && (
          <>
            <h4>{t('reportForm.multiMachineCsvImportModal.errorsTable')}</h4>
            <div className="spareparts-table-container">
              <table className="spareparts-table">
                <thead>
                  <tr>
                    <th>{t('reportForm.pantheraImportModal.colDocumentRow')}</th>
                    <th>{t('reportForm.pantheraImportModal.colCommessa')}</th>
                    <th>{t('reportForm.multiMachineCsvImportModal.colErrorCode')}</th>
                    <th>{t('reportForm.multiMachineCsvImportModal.colMessage')}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errors.map((err, i) => (
                    <tr key={i}>
                      <td>{err.rowNumber}</td>
                      <td>{err.serialNumber ?? '—'}</td>
                      <td>{t(`messages.csvImportErrorCode.${err.errorCode}`, err.errorCode)}</td>
                      <td>{err.errorMessage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-left">
            <h3>{t('reportForm.pantheraImportModal.title')}</h3>
          </div>
          {status !== 'waiting' && (
            <button onClick={onClose} className="modal-close">&times;</button>
          )}
        </div>

        <div className="modal-body">
          {status !== 'done' && (
            <div className="form-field">
              <label htmlFor="panthera-document-number">{t('reportForm.pantheraImportModal.documentNumber')}</label>
              <input
                id="panthera-document-number"
                type="text"
                value={documentNumber}
                placeholder="2026/VR 002149"
                onChange={(e) => setDocumentNumber(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && status === 'idle') handleImport(); }}
                disabled={status === 'waiting'}
                autoFocus
              />
              <p className="panthera-import-hint">{t('reportForm.pantheraImportModal.hint')}</p>
            </div>
          )}

          {status === 'idle' && renderNotice()}

          {status === 'waiting' && (
            <div className="csv-import-processing">
              <span className="btn-spinner"></span>
              <span>{t('reportForm.pantheraImportModal.waiting')}</span>
            </div>
          )}

          {status === 'done' && current && renderResult(current)}
        </div>

        {status === 'idle' && (
          <div className="modal-footer">
            {(notice === 'unreachable' || notice === 'panthera_error') && (
              <button type="button" className="btn-select-small" onClick={onSwitchToCsv}>
                {t('reportForm.pantheraImportModal.switchToCsv')}
              </button>
            )}
            <button type="button" className="btn-add" onClick={handleImport} disabled={!documentNumber.trim()}>
              <span className="material-icons">cloud_download</span>{' '}
              {notice ? t('reportForm.pantheraImportModal.retry') : t('reportForm.pantheraImportModal.import')}
            </button>
          </div>
        )}

        {status === 'done' && (
          <div className="modal-footer">
            <button type="button" className="btn-save" onClick={onClose}>
              {t('common.close')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PantheraImportModal;
